import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { getReliableThumbnailUrl, handleThumbnailError } from "../utils/youtubeThumbnail";
import { defaultSiteSettings, siteSettingItems, SiteSettings } from "../utils/siteSettings";
import { defaultSiteCopy, siteCopyItems, SiteCopy } from "../utils/siteCopy";

const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL =
    configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
        ? ""
        : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

type SectionId = "overview" | "content" | "contacts" | "categories" | "site" | "copy" | "stats";
type ContentType = "hero_video" | "works" | "product" | "pr";
type ThumbnailStatus = "idle" | "checking" | "valid" | "invalid";
type ContactStatus = "new" | "reviewing" | "replied" | "closed";
type CopyTabId = "home" | "works" | "portfolio" | "contact" | "detail" | "footer";
type StatsTabId = "summary" | "performance" | "quality";

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: ContentType;
    category_id?: number | null;
    category_name?: string | null;
    category_color?: string | null;
    youtube_url: string;
    youtube_id: string;
    thumbnail_url: string | null;
    duration: string | null;
    published_at: string | null;
    view_count: number;
    like_count: number;
    custom_thumbnail: string | null;
    custom_description: string | null;
    custom_published_at: string | null;
    is_active: boolean | number;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

interface Contact {
    id: number;
    name: string;
    email: string;
    phone?: string | null;
    company?: string | null;
    message: string;
    project_type?: string | null;
    budget?: string | null;
    is_read: boolean | number;
    admin_status?: ContactStatus | null;
    admin_note?: string | null;
    admin_updated_at?: string | null;
    created_at: string;
    updated_at?: string;
}

interface Category {
    id: number;
    name: string;
    description?: string | null;
    color?: string | null;
    is_active?: boolean | number;
    sort_order?: number;
}

interface Stats {
    works: number;
    product: number;
    pr: number;
    hero_video: number;
    contacts: number;
}

interface ContentForm {
    title: string;
    description: string;
    content_type: ContentType;
    category_id: string;
    youtube_url: string;
    custom_thumbnail: string;
    custom_description: string;
    custom_published_at: string;
    is_active: boolean;
    sort_order: number;
}

interface CategoryForm {
    name: string;
    description: string;
    color: string;
    sort_order: number;
}

interface ActivityItem {
    id: string;
    action: string;
    detail: string;
    createdAt: string;
}

interface QualityIssue {
    id: number;
    title: string;
    level: "주의" | "확인";
    issue: string;
}

const CONTENT_LABELS: Record<ContentType, string> = {
    hero_video: "Hero",
    works: "Works",
    product: "Product",
    pr: "PR",
};

const NAV_ITEMS: Array<{ id: SectionId; label: string; description: string }> = [
    { id: "overview", label: "대시보드", description: "핵심 현황" },
    { id: "content", label: "콘텐츠", description: "영상과 노출 상태" },
    { id: "contacts", label: "문의", description: "고객 메시지" },
    { id: "categories", label: "카테고리", description: "분류 체계" },
    { id: "site", label: "사이트 섹션", description: "포트폴리오 노출" },
    { id: "copy", label: "사이트 문구", description: "화면 문구 수정" },
    { id: "stats", label: "통계", description: "운영 지표" },
];

const COPY_TABS: Array<{ id: CopyTabId; label: string; keys: Array<keyof SiteCopy> }> = [
    { id: "home", label: "홈", keys: ["home_fallback_title", "home_fallback_subtitle"] },
    { id: "works", label: "Works", keys: ["works_intro_title", "works_intro_body"] },
    {
        id: "portfolio",
        label: "포트폴리오 섹션",
        keys: ["showreel_heading", "showreel_body", "featured_heading", "process_heading", "services_heading", "clients_heading", "clients_body", "contact_cta_heading", "contact_button_label"],
    },
    { id: "contact", label: "문의", keys: ["contacts_title", "contacts_subtitle"] },
    { id: "detail", label: "프로젝트 상세", keys: ["project_detail_hidden_title", "project_detail_hidden_body", "project_detail_case_heading", "project_detail_case_body"] },
    { id: "footer", label: "푸터", keys: ["footer_brand", "footer_tagline", "footer_naver_url", "footer_terms_label", "footer_privacy_label", "footer_admin_label"] },
];

const STATS_TABS: Array<{ id: StatsTabId; label: string }> = [
    { id: "summary", label: "요약" },
    { id: "performance", label: "성과" },
    { id: "quality", label: "품질" },
];

const getSectionFromParam = (value: string | null): SectionId => (
    NAV_ITEMS.some((item) => item.id === value) ? value as SectionId : "overview"
);

const CONTENT_PAGE_SIZE = 20;
const ACTIVITY_STORAGE_KEY = "noonAdminActivityLog";
const AUTH_EXPIRED_MESSAGE = "로그인 시간이 만료되었습니다. 다시 로그인해주세요.";

const emptyContentForm: ContentForm = {
    title: "",
    description: "",
    content_type: "works",
    category_id: "",
    youtube_url: "",
    custom_thumbnail: "",
    custom_description: "",
    custom_published_at: "",
    is_active: true,
    sort_order: 0,
};

const emptyCategoryForm: CategoryForm = {
    name: "",
    description: "",
    color: "#355f8f",
    sort_order: 0,
};

function getToken() {
    return localStorage.getItem("adminToken") || "";
}

function authConfig() {
    return { headers: { Authorization: `Bearer ${getToken()}` } };
}

function isAuthError(error: any) {
    return error?.response?.status === 401 || error?.response?.status === 403;
}

function isActive(value: boolean | number | undefined) {
    return value === true || value === 1;
}

function formatDate(value?: string | null) {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatDateTime(value?: string | null) {
    if (!value) return "-";
    return new Date(value).toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatNumber(value?: number) {
    return Number(value || 0).toLocaleString("ko-KR");
}

function formatViewMetric(value?: number) {
    const count = Number(value || 0);
    return count > 0 ? `${formatNumber(count)} views` : "조회수 미수집";
}

function toInputDateTime(value?: string | null) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function loadJson<T>(key: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
        return fallback;
    }
}

function makeContentPayload(item: Content, overrides: Partial<ContentForm> = {}) {
    const form: ContentForm = {
        title: item.title || "",
        description: item.description || "",
        content_type: item.content_type,
        category_id: item.category_id ? String(item.category_id) : "",
        youtube_url: item.youtube_url || "",
        custom_thumbnail: item.custom_thumbnail || "",
        custom_description: item.custom_description || "",
        custom_published_at: toInputDateTime(item.custom_published_at),
        is_active: isActive(item.is_active),
        sort_order: item.sort_order || 0,
        ...overrides,
    };

    return {
        title: form.title,
        description: form.description,
        content_type: form.content_type,
        category_id: form.category_id ? Number(form.category_id) : null,
        youtube_url: form.youtube_url,
        youtube_id: item.youtube_id || "",
        thumbnail_url: item.thumbnail_url || form.custom_thumbnail || null,
        duration: item.duration || null,
        published_at: item.published_at || null,
        view_count: Number(item.view_count || 0),
        like_count: Number(item.like_count || 0),
        custom_thumbnail: form.custom_thumbnail || null,
        custom_description: form.custom_description || null,
        custom_published_at: form.custom_published_at || null,
        is_active: form.is_active,
        sort_order: form.sort_order,
    };
}

const AdminShell = styled.div`
    min-height: 100vh;
    background: #f4f6f8;
    color: #18212f;
`;

const LoginScreen = styled.div`
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px;
    background: #eef2f6;
`;

const LoginPanel = styled.form`
    width: min(420px, 100%);
    background: #ffffff;
    border: 1px solid #dce3ea;
    border-radius: 8px;
    padding: 28px;
    box-shadow: 0 20px 50px rgba(16, 24, 32, 0.08);
`;

const LoginTitle = styled.h1`
    margin: 0;
    font-size: 1.6rem;
`;

const LoginSub = styled.p`
    margin: 6px 0 24px;
    color: #667085;
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr);
    min-height: 100vh;

    @media (max-width: 920px) {
        grid-template-columns: 1fr;
    }
`;

const Sidebar = styled.aside`
    background: #101820;
    color: #f8fafc;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 22px;
`;

const Brand = styled.div`
    padding: 6px 8px;
`;

const BrandName = styled.div`
    font-size: 1.05rem;
    font-weight: 800;
`;

const BrandMeta = styled.div`
    color: #9aa8b6;
    font-size: 0.78rem;
    margin-top: 4px;
`;

const NavList = styled.nav`
    display: grid;
    gap: 6px;
`;

const NavButton = styled.button<{ active: boolean }>`
    border: 0;
    border-radius: 6px;
    padding: 11px 10px;
    text-align: left;
    color: ${(props) => (props.active ? "#ffffff" : "#c6d0da")};
    background: ${(props) => (props.active ? "#234f7a" : "transparent")};
    cursor: pointer;
    display: grid;
    gap: 2px;

    &:hover {
        background: ${(props) => (props.active ? "#234f7a" : "#172431")};
    }
`;

const NavTitle = styled.span`
    font-size: 0.95rem;
    font-weight: 800;
`;

const NavDesc = styled.span`
    font-size: 0.74rem;
    color: #9aa8b6;
`;

const SidebarFooter = styled.div`
    margin-top: auto;
    display: grid;
    gap: 8px;
`;

const Main = styled.main`
    min-width: 0;
    padding: 22px;
`;

const Topbar = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: 720px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const PageTitle = styled.h1`
    margin: 0;
    font-size: 1.55rem;
`;

const PageMeta = styled.p`
    margin: 4px 0 0;
    color: #667085;
    font-size: 0.9rem;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const Button = styled.button<{ tone?: "primary" | "danger" | "quiet" }>`
    border: 1px solid ${(props) => (props.tone === "primary" ? "#234f7a" : props.tone === "danger" ? "#b42318" : "#cfd8e3")};
    background: ${(props) => (props.tone === "primary" ? "#234f7a" : props.tone === "danger" ? "#fff1f0" : "#ffffff")};
    color: ${(props) => (props.tone === "primary" ? "#ffffff" : props.tone === "danger" ? "#b42318" : "#344054")};
    border-radius: 6px;
    min-height: 38px;
    padding: 0 13px;
    font-weight: 800;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const Field = styled.div`
    display: grid;
    gap: 6px;
`;

const Label = styled.label`
    font-size: 0.8rem;
    color: #495566;
    font-weight: 800;
`;

const Input = styled.input`
    height: 38px;
    border: 1px solid #cfd8e3;
    border-radius: 6px;
    padding: 10px;
    background: #ffffff;
`;

const Select = styled.select`
    height: 38px;
    border: 1px solid #cfd8e3;
    border-radius: 6px;
    padding: 0 10px;
    background: #ffffff;
`;

const TextArea = styled.textarea`
    min-height: 96px;
    border: 1px solid #cfd8e3;
    border-radius: 6px;
    padding: 10px;
    resize: vertical;
`;

const SearchInput = styled(Input)`
    min-width: 260px;

    @media (max-width: 520px) {
        min-width: 100%;
    }
`;

const TabList = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-bottom: 1px solid #e6ebf0;
    background: #fbfcfe;
`;

const TabButton = styled.button<{ active?: boolean }>`
    min-height: 34px;
    border: 1px solid ${(props) => (props.active ? "#234f7a" : "#cfd8e3")};
    background: ${(props) => (props.active ? "#234f7a" : "#ffffff")};
    color: ${(props) => (props.active ? "#ffffff" : "#344054")};
    border-radius: 6px;
    padding: 0 12px;
    font-weight: 800;
    cursor: pointer;
`;

const FilterGrid = styled.div`
    padding: 12px 16px;
    border-bottom: 1px solid #e6ebf0;
    display: grid;
    grid-template-columns: 1.4fr repeat(4, minmax(130px, 0.7fr));
    gap: 10px;

    @media (max-width: 1040px) {
        grid-template-columns: 1fr 1fr;
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const Kpi = styled.button<{ selected?: boolean }>`
    min-height: 98px;
    border: 1px solid ${(props) => (props.selected ? "#234f7a" : "#dce3ea")};
    background: #ffffff;
    border-radius: 8px;
    padding: 14px;
    text-align: left;
    box-shadow: 0 8px 18px rgba(16, 24, 32, 0.04);
    cursor: pointer;
`;

const KpiValue = styled.div`
    font-size: 1.75rem;
    font-weight: 900;
    margin-bottom: 4px;
`;

const KpiLabel = styled.div`
    color: #667085;
    font-size: 0.84rem;
`;

const Panel = styled.section`
    background: #ffffff;
    border: 1px solid #dce3ea;
    border-radius: 8px;
    overflow: hidden;
`;

const PanelHeader = styled.div`
    min-height: 58px;
    padding: 14px 16px;
    border-bottom: 1px solid #e6ebf0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    @media (max-width: 720px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const PanelTitle = styled.h2`
    margin: 0;
    font-size: 1.02rem;
`;

const PanelSub = styled.div`
    color: #667085;
    font-size: 0.82rem;
    margin-top: 3px;
`;

const TableWrap = styled.div`
    overflow-x: auto;
`;

const Pagination = styled.div`
    min-height: 58px;
    padding: 12px 16px;
    border-top: 1px solid #e6ebf0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const PageInfo = styled.div`
    color: #667085;
    font-size: 0.82rem;
`;

const PageButtons = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const PageButton = styled.button<{ active?: boolean }>`
    min-width: 32px;
    height: 32px;
    border: 1px solid ${(props) => (props.active ? "#234f7a" : "#cfd8e3")};
    background: ${(props) => (props.active ? "#234f7a" : "#ffffff")};
    color: ${(props) => (props.active ? "#ffffff" : "#344054")};
    border-radius: 6px;
    font-weight: 800;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 1120px;
`;

const Th = styled.th`
    text-align: left;
    font-size: 0.76rem;
    color: #667085;
    background: #f8fafc;
    padding: 11px 14px;
    border-bottom: 1px solid #e6ebf0;
`;

const Td = styled.td`
    padding: 12px 14px;
    border-bottom: 1px solid #edf1f5;
    font-size: 0.86rem;
    vertical-align: middle;
`;

const DragRow = styled.tr<{ dragging?: boolean }>`
    opacity: ${(props) => (props.dragging ? 0.45 : 1)};

    &:hover {
        background: #f8fafc;
    }
`;

const Empty = styled.div`
    padding: 44px 16px;
    text-align: center;
    color: #667085;
`;

const Thumb = styled.img`
    width: 86px;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    object-fit: cover;
    background: #edf2f7;
    flex: 0 0 auto;
`;

const PreviewThumb = styled.img`
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    background: #edf2f7;
    border-radius: 8px;
`;

const RowTitle = styled.div`
    font-weight: 800;
    color: #18212f;
`;

const RowMeta = styled.div`
    margin-top: 4px;
    color: #667085;
    font-size: 0.78rem;
    max-width: 360px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Status = styled.span<{ active?: boolean; warn?: boolean }>`
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: 999px;
    padding: 0 9px;
    font-size: 0.75rem;
    font-weight: 800;
    color: ${(props) => (props.active ? "#05603a" : props.warn ? "#93370d" : "#5b6472")};
    background: ${(props) => (props.active ? "#d1fadf" : props.warn ? "#ffead5" : "#eef2f6")};
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const SmallButton = styled.button<{ tone?: "danger" | "primary" }>`
    border: 1px solid ${(props) => (props.tone === "danger" ? "#f1b8b3" : props.tone === "primary" ? "#b7d9f7" : "#cfd8e3")};
    background: ${(props) => (props.tone === "danger" ? "#fff8f7" : props.tone === "primary" ? "#eef7ff" : "#ffffff")};
    color: ${(props) => (props.tone === "danger" ? "#b42318" : props.tone === "primary" ? "#1f5d8f" : "#344054")};
    border-radius: 6px;
    height: 30px;
    padding: 0 9px;
    font-weight: 800;
    cursor: pointer;
`;

const Checkbox = styled.input`
    width: 16px;
    height: 16px;
`;

const BulkBar = styled.div`
    padding: 12px 16px;
    border-bottom: 1px solid #e6ebf0;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const Message = styled.div<{ tone: "error" | "success" }>`
    border: 1px solid ${(props) => (props.tone === "error" ? "#f1b8b3" : "#a6e7c0")};
    background: ${(props) => (props.tone === "error" ? "#fff8f7" : "#f0fff6")};
    color: ${(props) => (props.tone === "error" ? "#b42318" : "#05603a")};
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
    font-size: 0.88rem;
`;

const SplitGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
    gap: 16px;

    @media (max-width: 1040px) {
        grid-template-columns: 1fr;
    }
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const InfoCard = styled.div`
    border: 1px solid #e6ebf0;
    border-radius: 8px;
    padding: 14px;
    background: #ffffff;
`;

const DashboardStack = styled.div`
    display: grid;
    gap: 16px;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    padding: 16px;

    @media (max-width: 980px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const ChartList = styled.div`
    padding: 16px;
    display: grid;
    gap: 12px;
`;

const ChartRow = styled.div`
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr) 64px;
    align-items: center;
    gap: 12px;
    font-size: 0.86rem;
`;

const Track = styled.div`
    height: 10px;
    background: #edf2f7;
    border-radius: 999px;
    overflow: hidden;
`;

const Fill = styled.div<{ width: number }>`
    height: 100%;
    width: ${(props) => Math.min(100, Math.max(0, props.width))}%;
    background: #234f7a;
`;

const ActivityList = styled.div`
    padding: 16px;
    display: grid;
    gap: 10px;
`;

const ActivityRow = styled.div`
    display: grid;
    gap: 3px;
    border-bottom: 1px solid #edf1f5;
    padding-bottom: 10px;

    &:last-child {
        border-bottom: 0;
        padding-bottom: 0;
    }
`;

const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 35, 0.45);
    display: grid;
    place-items: center;
    padding: 20px;
    z-index: 1000;
`;

const ModalPanel = styled.div`
    width: min(780px, 100%);
    max-height: 92vh;
    overflow-y: auto;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #dce3ea;
`;

const ModalHeader = styled.div`
    padding: 16px;
    border-bottom: 1px solid #e6ebf0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const ModalTitle = styled.h2`
    margin: 0;
    font-size: 1.08rem;
`;

const FormGrid = styled.form`
    padding: 16px;
    display: grid;
    gap: 14px;
`;

const ModalBody = styled.div`
    padding: 16px;
    display: grid;
    gap: 14px;
`;

const TwoCols = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 680px) {
        grid-template-columns: 1fr;
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
`;

const ColorSwatch = styled.span<{ color?: string | null }>`
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background: ${(props) => props.color || "#8a95a3"};
    margin-right: 6px;
`;

const ToggleLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 800;
`;

const Admin: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoggedIn, setIsLoggedIn] = useState(Boolean(getToken()));
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [activeSection, setActiveSection] = useState<SectionId>(() => getSectionFromParam(searchParams.get("section")));
    const [copyTab, setCopyTab] = useState<CopyTabId>("home");
    const [statsTab, setStatsTab] = useState<StatsTabId>("summary");
    const [contentType, setContentType] = useState<ContentType | "all">("all");
    const [search, setSearch] = useState("");
    const [contentPage, setContentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive" | "issue">("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [showUnusedCategories, setShowUnusedCategories] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [selectedContentIds, setSelectedContentIds] = useState<number[]>([]);
    const [bulkCategoryId, setBulkCategoryId] = useState("");
    const [draggingContentId, setDraggingContentId] = useState<number | null>(null);
    const [draggingCategoryId, setDraggingCategoryId] = useState<number | null>(null);
    const [thumbnailStatus, setThumbnailStatus] = useState<ThumbnailStatus>("idle");
    const [previewContent, setPreviewContent] = useState<Content | null>(null);
    const [activityLog, setActivityLog] = useState<ActivityItem[]>(() => loadJson<ActivityItem[]>(ACTIVITY_STORAGE_KEY, []));
    const [stats, setStats] = useState<Stats>({ works: 0, product: 0, pr: 0, hero_video: 0, contacts: 0 });
    const [siteSettings, setSiteSettings] = useState<SiteSettings>(defaultSiteSettings);
    const [siteCopy, setSiteCopy] = useState<SiteCopy>(defaultSiteCopy);
    const [content, setContent] = useState<Content[]>([]);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [contentModalOpen, setContentModalOpen] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editingContent, setEditingContent] = useState<Content | null>(null);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [contentForm, setContentForm] = useState<ContentForm>(emptyContentForm);
    const [categoryForm, setCategoryForm] = useState<CategoryForm>(emptyCategoryForm);

    const totalContent = content.length;
    const activeContent = content.filter((item) => isActive(item.is_active)).length;
    const unreadContacts = contacts.filter((item) => !isActive(item.is_read)).length;

    const qualityIssues = useMemo<QualityIssue[]>(() => {
        return content.flatMap((item) => {
            const issues: QualityIssue[] = [];
            if (!item.category_id && item.content_type !== "hero_video") {
                issues.push({ id: item.id, title: item.title, level: "확인", issue: "카테고리 없음" });
            }
            if (!item.custom_thumbnail && !item.thumbnail_url) {
                issues.push({ id: item.id, title: item.title, level: "주의", issue: "썸네일 없음" });
            }
            if (!item.description && !item.custom_description) {
                issues.push({ id: item.id, title: item.title, level: "확인", issue: "설명 없음" });
            }
            if (!item.youtube_url) {
                issues.push({ id: item.id, title: item.title, level: "주의", issue: "YouTube URL 없음" });
            }
            return issues;
        });
    }, [content]);

    const filteredContent = useMemo(() => {
        return content
            .filter((item) => contentType === "all" || item.content_type === contentType)
            .filter((item) => categoryFilter === "all" || String(item.category_id || "") === categoryFilter)
            .filter((item) => {
                if (statusFilter === "active") return isActive(item.is_active);
                if (statusFilter === "inactive") return !isActive(item.is_active);
                if (statusFilter === "issue") return qualityIssues.some((issue) => issue.id === item.id);
                return true;
            })
            .filter((item) => {
                const created = new Date(item.created_at).getTime();
                if (dateFrom && created < new Date(dateFrom).getTime()) return false;
                if (dateTo && created > new Date(`${dateTo}T23:59:59`).getTime()) return false;
                return true;
            })
            .filter((item) => {
                const keyword = search.trim().toLowerCase();
                if (!keyword) return true;
                return [item.title, item.description, item.custom_description, item.category_name, item.youtube_url, item.youtube_id]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(keyword));
            })
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || b.id - a.id);
    }, [categoryFilter, content, contentType, dateFrom, dateTo, qualityIssues, search, statusFilter]);

    const totalContentPages = Math.max(1, Math.ceil(filteredContent.length / CONTENT_PAGE_SIZE));
    const visibleContent = useMemo(() => {
        const start = (contentPage - 1) * CONTENT_PAGE_SIZE;
        return filteredContent.slice(start, start + CONTENT_PAGE_SIZE);
    }, [contentPage, filteredContent]);

    const contentCounts = useMemo(() => {
        return content.reduce<Record<ContentType, number>>(
            (acc, item) => {
                acc[item.content_type] += 1;
                return acc;
            },
            { hero_video: 0, works: 0, product: 0, pr: 0 }
        );
    }, [content]);

    const categoryUsage = useMemo(() => {
        return categories.map((category) => ({
            ...category,
            count: content.filter((item) => item.category_id === category.id).length,
        }));
    }, [categories, content]);
    const visibleCategoryUsage = useMemo(
        () => categoryUsage.filter((category) => showUnusedCategories || category.count > 0),
        [categoryUsage, showUnusedCategories]
    );

    const selectedItems = useMemo(
        () => content.filter((item) => selectedContentIds.includes(item.id)),
        [content, selectedContentIds]
    );

    const navigateSection = (section: SectionId) => {
        setActiveSection(section);
        const nextParams = new URLSearchParams(searchParams);
        if (section === "overview") {
            nextParams.delete("section");
        } else {
            nextParams.set("section", section);
        }
        setSearchParams(nextParams, { replace: false });
    };

    useEffect(() => {
        const nextSection = getSectionFromParam(searchParams.get("section"));
        setActiveSection((current) => current === nextSection ? current : nextSection);
    }, [searchParams]);

    useEffect(() => {
        if (isLoggedIn) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    useEffect(() => {
        setContentPage(1);
        setSelectedContentIds([]);
    }, [categoryFilter, contentType, dateFrom, dateTo, search, statusFilter]);

    useEffect(() => {
        setContentPage((page) => Math.min(page, totalContentPages));
    }, [totalContentPages]);

    useEffect(() => {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, JSON.stringify(activityLog.slice(0, 50)));
    }, [activityLog]);

    const clearNotice = () => {
        setError("");
        setSuccess("");
    };

    const pushActivity = (action: string, detail: string) => {
        setActivityLog((items) => [{ id: `${Date.now()}-${Math.random()}`, action, detail, createdAt: new Date().toISOString() }, ...items].slice(0, 50));
    };

    const expireSession = () => {
        localStorage.removeItem("adminToken");
        setIsLoggedIn(false);
        setContentModalOpen(false);
        setCategoryModalOpen(false);
        setPreviewContent(null);
        setError(AUTH_EXPIRED_MESSAGE);
    };

    const setRequestError = (requestError: any, fallbackMessage: string) => {
        if (isAuthError(requestError)) {
            expireSession();
            return true;
        }
        setError(requestError.response?.data?.error || fallbackMessage);
        return false;
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const [statsRes, contentRes, contactsRes, categoriesRes, settingsRes, copyRes] = await Promise.all([
                axios.get("/api/admin/stats", authConfig()),
                axios.get("/api/content", authConfig()),
                axios.get("/api/admin/contacts", authConfig()),
                axios.get("/api/admin/categories", authConfig()),
                axios.get("/api/site-settings"),
                axios.get("/api/site-copy"),
            ]);
            setStats(statsRes.data.data || stats);
            setContent(contentRes.data.data || []);
            setContacts(contactsRes.data.contacts || contactsRes.data.data || []);
            setCategories(categoriesRes.data.data || []);
            setSiteSettings({ ...defaultSiteSettings, ...(settingsRes.data.data || {}) });
            setSiteCopy({ ...defaultSiteCopy, ...(copyRes.data.data || {}) });
        } catch (fetchError: any) {
            setRequestError(fetchError, "데이터를 불러오지 못했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const updateSiteSetting = async (key: keyof SiteSettings, value: boolean) => {
        const previous = siteSettings;
        const next = { ...siteSettings, [key]: value };
        setSiteSettings(next);
        clearNotice();

        try {
            setIsLoading(true);
            const response = await axios.put("/api/admin/site-settings", next, authConfig());
            setSiteSettings({ ...defaultSiteSettings, ...(response.data.data || next) });
            setSuccess("사이트 섹션 설정을 저장했습니다.");
            pushActivity("사이트 섹션", `${siteSettingItems.find((item) => item.key === key)?.title || key} ${value ? "노출" : "숨김"}`);
        } catch (settingsError: any) {
            setSiteSettings(previous);
            setRequestError(settingsError, "사이트 섹션 설정 저장에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const saveSiteCopy = async () => {
        clearNotice();
        try {
            setIsLoading(true);
            const response = await axios.put("/api/admin/site-copy", siteCopy, authConfig());
            setSiteCopy({ ...defaultSiteCopy, ...(response.data.data || siteCopy) });
            setSuccess("사이트 문구를 저장했습니다.");
            pushActivity("사이트 문구", "공개 화면 문구 업데이트");
        } catch (copyError: any) {
            setRequestError(copyError, "사이트 문구 저장에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async (event: React.FormEvent) => {
        event.preventDefault();
        clearNotice();
        try {
            setIsLoading(true);
            const response = await axios.post("/api/admin/login", { username, password });
            if (!response.data.success) {
                setError(response.data.error || "로그인에 실패했습니다.");
                return;
            }
            localStorage.setItem("adminToken", response.data.token);
            setIsLoggedIn(true);
            pushActivity("로그인", username || "관리자");
        } catch (loginError: any) {
            setError(loginError.response?.data?.error || "로그인에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("adminToken");
        setIsLoggedIn(false);
    };

    const fetchYouTubeInfo = async () => {
        if (!contentForm.youtube_url) {
            setError("YouTube URL을 입력하세요.");
            return;
        }
        clearNotice();
        try {
            setIsLoading(true);
            const response = await axios.post("/api/admin/youtube-info", { youtube_url: contentForm.youtube_url }, authConfig());
            const data = response.data.data || {};
            setContentForm((form) => ({
                ...form,
                title: form.title || data.title || "",
                description: form.description || data.description || "",
                custom_thumbnail: form.custom_thumbnail || data.thumbnail_url || "",
            }));
            setSuccess("YouTube 정보를 가져왔습니다.");
            pushActivity("YouTube 조회", data.title || contentForm.youtube_url);
        } catch (youtubeError: any) {
            setRequestError(youtubeError, "YouTube 정보를 가져오지 못했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const validateThumbnail = (url = contentForm.custom_thumbnail) => {
        if (!url) {
            setThumbnailStatus("invalid");
            return;
        }
        setThumbnailStatus("checking");
        const image = new Image();
        image.onload = () => setThumbnailStatus("valid");
        image.onerror = () => setThumbnailStatus("invalid");
        image.src = getReliableThumbnailUrl(url);
    };

    const openNewContent = (type: ContentType = "works") => {
        clearNotice();
        setEditingContent(null);
        setThumbnailStatus("idle");
        setContentForm({ ...emptyContentForm, content_type: type });
        setContentModalOpen(true);
    };

    const openEditContent = (item: Content) => {
        clearNotice();
        setEditingContent(item);
        setThumbnailStatus("idle");
        setContentForm({
            title: item.title || "",
            description: item.description || "",
            content_type: item.content_type,
            category_id: item.category_id ? String(item.category_id) : "",
            youtube_url: item.youtube_url || "",
            custom_thumbnail: item.custom_thumbnail || item.thumbnail_url || "",
            custom_description: item.custom_description || "",
            custom_published_at: toInputDateTime(item.custom_published_at),
            is_active: isActive(item.is_active),
            sort_order: item.sort_order || 0,
        });
        setContentModalOpen(true);
    };

    const submitContent = async (event: React.FormEvent) => {
        event.preventDefault();
        clearNotice();
        try {
            setIsLoading(true);
            const payload = makeContentPayload(
                editingContent || ({
                    ...emptyContentForm,
                    id: 0,
                    thumbnail_url: null,
                    duration: null,
                    published_at: null,
                    view_count: 0,
                    like_count: 0,
                    created_at: "",
                    updated_at: "",
                } as unknown as Content),
                contentForm
            );
            if (editingContent) {
                await axios.put(`/api/admin/content/${editingContent.id}`, payload, authConfig());
                pushActivity("콘텐츠 수정", contentForm.title);
                setSuccess("콘텐츠가 수정되었습니다.");
            } else {
                await axios.post("/api/admin/content", payload, authConfig());
                pushActivity("콘텐츠 추가", contentForm.title);
                setSuccess("콘텐츠가 추가되었습니다.");
            }
            setContentModalOpen(false);
            await fetchData();
        } catch (submitError: any) {
            setRequestError(submitError, "콘텐츠 저장에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const deleteContent = async (id: number) => {
        const target = content.find((item) => item.id === id);
        if (!window.confirm("콘텐츠를 삭제하시겠습니까?")) return;
        clearNotice();
        try {
            setIsLoading(true);
            await axios.delete(`/api/admin/content/${id}`, authConfig());
            pushActivity("콘텐츠 삭제", target?.title || String(id));
            setSuccess("콘텐츠가 삭제되었습니다.");
            await fetchData();
        } catch (deleteError: any) {
            setRequestError(deleteError, "콘텐츠 삭제에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleContent = async (id: number) => {
        const target = content.find((item) => item.id === id);
        clearNotice();
        try {
            setIsLoading(true);
            await axios.patch(`/api/admin/content/${id}/toggle`, {}, authConfig());
            pushActivity("노출 변경", target?.title || String(id));
            await fetchData();
        } catch (toggleError: any) {
            setRequestError(toggleError, "노출 상태 변경에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const updateContentOrder = async (dragId: number, dropId: number) => {
        if (dragId === dropId) return;
        const ordered = [...filteredContent];
        const from = ordered.findIndex((item) => item.id === dragId);
        const to = ordered.findIndex((item) => item.id === dropId);
        if (from < 0 || to < 0) return;
        const [moved] = ordered.splice(from, 1);
        ordered.splice(to, 0, moved);
        const nextContent = content.map((item) => {
            const index = ordered.findIndex((orderedItem) => orderedItem.id === item.id);
            return index >= 0 ? { ...item, sort_order: index + 1 } : item;
        });
        setContent(nextContent);
        clearNotice();
        try {
            setIsLoading(true);
            await Promise.all(ordered.map((item, index) => axios.put(`/api/admin/content/${item.id}`, makeContentPayload(item, { sort_order: index + 1 }), authConfig())));
            pushActivity("정렬 변경", `${ordered.length}개 콘텐츠 순서 저장`);
            setSuccess("콘텐츠 순서가 저장되었습니다.");
            await fetchData();
        } catch (orderError: any) {
            if (!setRequestError(orderError, "순서 저장에 실패했습니다.")) {
                await fetchData();
            }
        } finally {
            setIsLoading(false);
        }
    };

    const updateCategoryOrder = async (dragId: number, dropId: number) => {
        if (dragId === dropId) return;

        const ordered = [...visibleCategoryUsage];
        const from = ordered.findIndex((item) => item.id === dragId);
        const to = ordered.findIndex((item) => item.id === dropId);
        if (from < 0 || to < 0) return;

        const [moved] = ordered.splice(from, 1);
        ordered.splice(to, 0, moved);

        const nextCategories = categories.map((item) => {
            const index = ordered.findIndex((orderedItem) => orderedItem.id === item.id);
            return index >= 0 ? { ...item, sort_order: index + 1 } : item;
        });
        setCategories(nextCategories);
        clearNotice();

        try {
            setIsLoading(true);
            await axios.patch(
                "/api/categories/order",
                {
                    items: ordered.map((item, index) => ({
                        id: item.id,
                        sort_order: index + 1,
                    })),
                },
                authConfig()
            );
            pushActivity("카테고리 정렬 변경", `${ordered.length}개 카테고리 순서 저장`);
            setSuccess("카테고리 순서가 저장되었습니다.");
            await fetchData();
        } catch (orderError: any) {
            if (!setRequestError(orderError, "카테고리 순서 저장에 실패했습니다.")) {
                await fetchData();
            }
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSelectContent = (id: number) => {
        setSelectedContentIds((ids) => (ids.includes(id) ? ids.filter((itemId) => itemId !== id) : [...ids, id]));
    };

    const selectVisibleContent = () => {
        const visibleIds = visibleContent.map((item) => item.id);
        setSelectedContentIds((ids) => Array.from(new Set([...ids, ...visibleIds])));
    };

    const runBulkUpdate = async (mode: "show" | "hide" | "category" | "delete") => {
        if (selectedItems.length === 0) return;
        if (mode === "delete" && !window.confirm(`${selectedItems.length}개 콘텐츠를 삭제하시겠습니까?`)) return;
        clearNotice();
        try {
            setIsLoading(true);
            if (mode === "delete") {
                await Promise.all(selectedItems.map((item) => axios.delete(`/api/admin/content/${item.id}`, authConfig())));
            } else {
                await Promise.all(
                    selectedItems.map((item) => {
                        const overrides: Partial<ContentForm> =
                            mode === "category"
                                ? { category_id: bulkCategoryId }
                                : { is_active: mode === "show" };
                        return axios.put(`/api/admin/content/${item.id}`, makeContentPayload(item, overrides), authConfig());
                    })
                );
            }
            pushActivity("대량 작업", `${selectedItems.length}개 ${mode === "show" ? "노출" : mode === "hide" ? "숨김" : mode === "category" ? "분류 변경" : "삭제"}`);
            setSelectedContentIds([]);
            setSuccess("대량 작업이 완료되었습니다.");
            await fetchData();
        } catch (bulkError: any) {
            setRequestError(bulkError, "대량 작업에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const openNewCategory = () => {
        clearNotice();
        setEditingCategory(null);
        setCategoryForm(emptyCategoryForm);
        setCategoryModalOpen(true);
    };

    const openEditCategory = (category: Category) => {
        clearNotice();
        setEditingCategory(category);
        setCategoryForm({
            name: category.name || "",
            description: category.description || "",
            color: category.color || "#355f8f",
            sort_order: category.sort_order || 0,
        });
        setCategoryModalOpen(true);
    };

    const submitCategory = async (event: React.FormEvent) => {
        event.preventDefault();
        clearNotice();
        try {
            setIsLoading(true);
            if (editingCategory) {
                await axios.put(`/api/categories/${editingCategory.id}`, categoryForm, authConfig());
                pushActivity("카테고리 수정", categoryForm.name);
            } else {
                await axios.post("/api/categories", categoryForm, authConfig());
                pushActivity("카테고리 추가", categoryForm.name);
            }
            setSuccess("카테고리가 저장되었습니다.");
            setCategoryModalOpen(false);
            await fetchData();
        } catch (categoryError: any) {
            setRequestError(categoryError, "카테고리 저장에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const deleteCategory = async (id: number) => {
        const target = categories.find((item) => item.id === id);
        if (!window.confirm("카테고리를 삭제하시겠습니까?")) return;
        clearNotice();
        try {
            setIsLoading(true);
            await axios.delete(`/api/categories/${id}`, authConfig());
            pushActivity("카테고리 삭제", target?.name || String(id));
            setSuccess("카테고리가 삭제되었습니다.");
            await fetchData();
        } catch (categoryError: any) {
            setRequestError(categoryError, "카테고리 삭제에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleCategory = async (id: number) => {
        const target = categories.find((item) => item.id === id);
        clearNotice();
        try {
            setIsLoading(true);
            await axios.patch(`/api/categories/${id}/toggle`, {}, authConfig());
            pushActivity("카테고리 노출 변경", target?.name || String(id));
            setSuccess("카테고리 노출 상태가 변경되었습니다.");
            await fetchData();
        } catch (categoryError: any) {
            setRequestError(categoryError, "카테고리 노출 상태 변경에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const markContactAsRead = async (id: number) => {
        clearNotice();
        try {
            setIsLoading(true);
            await axios.patch(`/api/admin/contacts/${id}/read`, {}, authConfig());
            pushActivity("문의 확인", String(id));
            await fetchData();
        } catch (contactError: any) {
            setRequestError(contactError, "문의 상태 변경에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const deleteContact = async (id: number) => {
        if (!window.confirm("문의를 삭제하시겠습니까?")) return;
        clearNotice();
        try {
            setIsLoading(true);
            await axios.delete(`/api/admin/contacts/${id}`, authConfig());
            pushActivity("문의 삭제", String(id));
            setSuccess("문의가 삭제되었습니다.");
            await fetchData();
        } catch (contactError: any) {
            setRequestError(contactError, "문의 삭제에 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const updateContactMeta = async (id: number, patch: Partial<{ status: ContactStatus; note: string }>) => {
        const current = contacts.find((contact) => contact.id === id);
        const nextStatus = patch.status || current?.admin_status || "new";
        const nextNote = patch.note ?? current?.admin_note ?? "";

        setContacts((items) =>
            items.map((contact) =>
                contact.id === id
                    ? {
                          ...contact,
                          admin_status: nextStatus,
                          admin_note: nextNote,
                          is_read: true,
                          admin_updated_at: new Date().toISOString(),
                      }
                    : contact
            )
        );

        try {
            await axios.patch(
                `/api/admin/contacts/${id}/meta`,
                {
                    admin_status: nextStatus,
                    admin_note: nextNote,
                },
                authConfig()
            );
            pushActivity("문의 메모", String(id));
        } catch (contactError: any) {
            if (!setRequestError(contactError, "문의 메모 저장에 실패했습니다.")) {
                await fetchData();
            }
        }
    };

    const renderKpis = () => {
        const items = [
            { label: "전체 콘텐츠", value: totalContent, section: "content" as SectionId },
            { label: "노출 콘텐츠", value: activeContent, section: "content" as SectionId },
            { label: "미확인 문의", value: unreadContacts, section: "contacts" as SectionId },
            { label: "품질 확인", value: qualityIssues.length, section: "stats" as SectionId },
            { label: "카테고리", value: categories.length, section: "categories" as SectionId },
        ];
        return (
            <KpiGrid>
                {items.map((item) => (
                    <Kpi key={item.label} selected={activeSection === item.section} onClick={() => navigateSection(item.section)}>
                        <KpiValue>{formatNumber(item.value)}</KpiValue>
                        <KpiLabel>{item.label}</KpiLabel>
                    </Kpi>
                ))}
            </KpiGrid>
        );
    };

    const renderContentPagination = () => {
        const start = filteredContent.length === 0 ? 0 : (contentPage - 1) * CONTENT_PAGE_SIZE + 1;
        const end = Math.min(filteredContent.length, contentPage * CONTENT_PAGE_SIZE);
        const firstPage = Math.max(1, Math.min(contentPage - 2, totalContentPages - 4));
        const pageNumbers = Array.from({ length: Math.min(5, totalContentPages) }, (_, index) => firstPage + index);

        return (
            <Pagination>
                <PageInfo>
                    {formatNumber(filteredContent.length)}개 중 {formatNumber(start)}-{formatNumber(end)} 표시
                </PageInfo>
                <PageButtons>
                    <PageButton disabled={contentPage === 1} onClick={() => setContentPage(1)}>처음</PageButton>
                    <PageButton disabled={contentPage === 1} onClick={() => setContentPage((page) => Math.max(1, page - 1))}>이전</PageButton>
                    {pageNumbers.map((page) => (
                        <PageButton key={page} active={page === contentPage} onClick={() => setContentPage(page)}>{page}</PageButton>
                    ))}
                    <PageButton disabled={contentPage === totalContentPages} onClick={() => setContentPage((page) => Math.min(totalContentPages, page + 1))}>다음</PageButton>
                    <PageButton disabled={contentPage === totalContentPages} onClick={() => setContentPage(totalContentPages)}>끝</PageButton>
                </PageButtons>
            </Pagination>
        );
    };

    const renderContentFilters = () => (
        <FilterGrid>
            <SearchInput value={search} onChange={(event) => setSearch(event.target.value)} placeholder="제목, 설명, 카테고리, URL 검색" />
            <Select value={contentType} onChange={(event) => setContentType(event.target.value as ContentType | "all")}>
                <option value="all">전체 유형</option>
                {Object.entries(CONTENT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                ))}
            </Select>
            <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "all" | "active" | "inactive" | "issue")}>
                <option value="all">전체 상태</option>
                <option value="active">노출</option>
                <option value="inactive">비노출</option>
                <option value="issue">품질 확인</option>
            </Select>
            <Select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
                <option value="all">전체 카테고리</option>
                {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                ))}
            </Select>
            <Toolbar>
                <Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                <Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            </Toolbar>
        </FilterGrid>
    );

    const renderBulkBar = () => (
        <BulkBar>
            <Toolbar>
                <Button onClick={selectVisibleContent}>현재 페이지 선택</Button>
                <Button onClick={() => setSelectedContentIds([])}>선택 해제</Button>
                <span>{formatNumber(selectedContentIds.length)}개 선택됨</span>
            </Toolbar>
            <Toolbar>
                <Button disabled={selectedItems.length === 0} onClick={() => runBulkUpdate("show")}>노출</Button>
                <Button disabled={selectedItems.length === 0} onClick={() => runBulkUpdate("hide")}>숨김</Button>
                <Select value={bulkCategoryId} onChange={(event) => setBulkCategoryId(event.target.value)}>
                    <option value="">카테고리 없음</option>
                    {categories.map((category) => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                </Select>
                <Button disabled={selectedItems.length === 0} onClick={() => runBulkUpdate("category")}>분류 적용</Button>
                <Button tone="danger" disabled={selectedItems.length === 0} onClick={() => runBulkUpdate("delete")}>삭제</Button>
            </Toolbar>
        </BulkBar>
    );

    const renderContentTable = () => (
        <Panel>
            <PanelHeader>
                <div>
                    <PanelTitle>콘텐츠 관리</PanelTitle>
                    <PanelSub>페이지당 20개, 드래그로 순서 변경</PanelSub>
                </div>
                <Toolbar>
                    <Button onClick={fetchData}>새로고침</Button>
                    <Button tone="primary" onClick={() => openNewContent(contentType === "all" ? "works" : contentType)}>추가</Button>
                </Toolbar>
            </PanelHeader>
            {renderContentFilters()}
            {renderBulkBar()}
            {filteredContent.length === 0 ? (
                <Empty>표시할 콘텐츠가 없습니다.</Empty>
            ) : (
                <TableWrap>
                    <Table>
                        <thead>
                            <tr>
                                <Th><Checkbox type="checkbox" checked={visibleContent.length > 0 && visibleContent.every((item) => selectedContentIds.includes(item.id))} onChange={selectVisibleContent} /></Th>
                                <Th>콘텐츠</Th>
                                <Th>유형</Th>
                                <Th>카테고리</Th>
                                <Th>상태</Th>
                                <Th>성과</Th>
                                <Th>등록일</Th>
                                <Th>작업</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleContent.map((item) => (
                                <DragRow
                                    key={item.id}
                                    draggable
                                    dragging={draggingContentId === item.id}
                                    onDragStart={() => setDraggingContentId(item.id)}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={() => {
                                        if (draggingContentId) updateContentOrder(draggingContentId, item.id);
                                        setDraggingContentId(null);
                                    }}
                                    onDragEnd={() => setDraggingContentId(null)}
                                >
                                    <Td><Checkbox type="checkbox" checked={selectedContentIds.includes(item.id)} onChange={() => toggleSelectContent(item.id)} /></Td>
                                    <Td>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <Thumb src={getReliableThumbnailUrl(item.custom_thumbnail || item.thumbnail_url)} alt={item.title} onError={(event) => handleThumbnailError(event.currentTarget)} />
                                            <div>
                                                <RowTitle>{item.title}</RowTitle>
                                                <RowMeta>{item.youtube_id || item.youtube_url || "-"}</RowMeta>
                                            </div>
                                        </div>
                                    </Td>
                                    <Td>{CONTENT_LABELS[item.content_type]}</Td>
                                    <Td>
                                        {item.category_name ? (
                                            <>
                                                <ColorSwatch color={item.category_color} />
                                                {item.category_name}
                                            </>
                                        ) : "-"}
                                    </Td>
                                    <Td><Status active={isActive(item.is_active)}>{isActive(item.is_active) ? "노출" : "비노출"}</Status></Td>
                                            <Td>{formatViewMetric(item.view_count)}</Td>
                                    <Td>{formatDate(item.created_at)}</Td>
                                    <Td>
                                        <Actions>
                                            <SmallButton onClick={() => setPreviewContent(item)}>미리보기</SmallButton>
                                            <SmallButton tone="primary" onClick={() => openEditContent(item)}>수정</SmallButton>
                                            <SmallButton onClick={() => toggleContent(item.id)}>{isActive(item.is_active) ? "숨김" : "노출"}</SmallButton>
                                            <SmallButton tone="danger" onClick={() => deleteContent(item.id)}>삭제</SmallButton>
                                        </Actions>
                                    </Td>
                                </DragRow>
                            ))}
                        </tbody>
                    </Table>
                    {renderContentPagination()}
                </TableWrap>
            )}
        </Panel>
    );

    const renderContacts = () => (
        <Panel>
            <PanelHeader>
                <div>
                    <PanelTitle>문의 관리</PanelTitle>
                    <PanelSub>{formatNumber(contacts.length)}건 중 {formatNumber(unreadContacts)}건 미확인</PanelSub>
                </div>
                <Button onClick={fetchData}>새로고침</Button>
            </PanelHeader>
            {contacts.length === 0 ? (
                <Empty>접수된 문의가 없습니다.</Empty>
            ) : (
                <TableWrap>
                    <Table>
                        <thead>
                            <tr>
                                <Th>상태</Th>
                                <Th>보낸 사람</Th>
                                <Th>메시지</Th>
                                <Th>처리</Th>
                                <Th>내부 메모</Th>
                                <Th>접수일</Th>
                                <Th>작업</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {contacts.map((contact) => {
                                const meta = {
                                    status: contact.admin_status || "new" as ContactStatus,
                                    note: contact.admin_note || "",
                                };
                                return (
                                    <tr key={contact.id}>
                                        <Td><Status active={isActive(contact.is_read)} warn={!isActive(contact.is_read)}>{isActive(contact.is_read) ? "확인" : "신규"}</Status></Td>
                                        <Td>
                                            <RowTitle>{contact.name}</RowTitle>
                                            <RowMeta>{contact.email}</RowMeta>
                                            <RowMeta>{contact.phone || contact.company || "-"}</RowMeta>
                                        </Td>
                                        <Td style={{ maxWidth: 380 }}>{contact.message}</Td>
                                        <Td>
                                            <Select value={meta.status} onChange={(event) => updateContactMeta(contact.id, { status: event.target.value as ContactStatus })}>
                                                <option value="new">신규</option>
                                                <option value="reviewing">검토</option>
                                                <option value="replied">답변완료</option>
                                                <option value="closed">종료</option>
                                            </Select>
                                        </Td>
                                        <Td>
                                            <Input value={meta.note} onChange={(event) => updateContactMeta(contact.id, { note: event.target.value })} placeholder="내부 메모" />
                                        </Td>
                                        <Td>{formatDate(contact.created_at)}</Td>
                                        <Td>
                                            <Actions>
                                                {!isActive(contact.is_read) && <SmallButton tone="primary" onClick={() => markContactAsRead(contact.id)}>확인</SmallButton>}
                                                <SmallButton tone="danger" onClick={() => deleteContact(contact.id)}>삭제</SmallButton>
                                            </Actions>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </TableWrap>
            )}
        </Panel>
    );

    const renderCategories = () => (
        <Panel>
            <PanelHeader>
                <div>
                    <PanelTitle>카테고리 관리</PanelTitle>
                    <PanelSub>
                        사용 중 {formatNumber(categoryUsage.filter((category) => category.count > 0).length)}개
                        {showUnusedCategories ? ` · 전체 ${formatNumber(categories.length)}개 표시` : " · 미사용 숨김"}
                    </PanelSub>
                </div>
                <Toolbar>
                    <Button onClick={() => setShowUnusedCategories((value) => !value)}>
                        {showUnusedCategories ? "미사용 숨기기" : "미사용 보기"}
                    </Button>
                    <Button tone="primary" onClick={openNewCategory}>추가</Button>
                </Toolbar>
            </PanelHeader>
            {visibleCategoryUsage.length === 0 ? (
                <Empty>등록된 카테고리가 없습니다.</Empty>
            ) : (
                <TableWrap>
                    <Table>
                        <thead>
                            <tr>
                                <Th>이름</Th>
                                <Th>설명</Th>
                                <Th>사용 콘텐츠</Th>
                                <Th>상태</Th>
                                <Th>정렬</Th>
                                <Th>작업</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleCategoryUsage.map((category) => (
                                <DragRow
                                    key={category.id}
                                    draggable
                                    dragging={draggingCategoryId === category.id}
                                    onDragStart={() => setDraggingCategoryId(category.id)}
                                    onDragEnd={() => setDraggingCategoryId(null)}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={() => {
                                        if (draggingCategoryId) updateCategoryOrder(draggingCategoryId, category.id);
                                        setDraggingCategoryId(null);
                                    }}
                                >
                                    <Td><ColorSwatch color={category.color} /><strong>{category.name}</strong></Td>
                                    <Td>{category.description || "-"}</Td>
                                    <Td>{formatNumber(category.count)}</Td>
                                    <Td><Status active={isActive(category.is_active)}>{isActive(category.is_active) ? "공개" : "미노출"}</Status></Td>
                                    <Td>{category.sort_order || 0}</Td>
                                    <Td>
                                        <Actions>
                                            <SmallButton tone="primary" onClick={() => openEditCategory(category)}>수정</SmallButton>
                                            <SmallButton onClick={() => toggleCategory(category.id)}>{isActive(category.is_active) ? "미노출" : "공개"}</SmallButton>
                                            <SmallButton tone="danger" onClick={() => deleteCategory(category.id)}>삭제</SmallButton>
                                        </Actions>
                                    </Td>
                                </DragRow>
                            ))}
                        </tbody>
                    </Table>
                </TableWrap>
            )}
        </Panel>
    );

    const renderSiteSettings = () => (
        <Panel>
            <PanelHeader>
                <div>
                    <PanelTitle>사이트 섹션 설정</PanelTitle>
                    <PanelSub>사용자 화면에 표시할 기능을 켜고 끕니다. 문구 수정은 사이트 문구 메뉴에서 진행합니다.</PanelSub>
                </div>
                <Button onClick={fetchData}>새로고침</Button>
            </PanelHeader>
            <CardGrid>
                {siteSettingItems.map((item) => (
                    <InfoCard key={item.key}>
                        <RowMeta>{item.priority}</RowMeta>
                        <RowTitle>{item.title}</RowTitle>
                        <p style={{ margin: "8px 0 14px", color: "#667085", lineHeight: 1.55 }}>{item.description}</p>
                        <ToggleLabel>
                            <Checkbox
                                type="checkbox"
                                checked={siteSettings[item.key]}
                                onChange={(event) => updateSiteSetting(item.key, event.target.checked)}
                                disabled={isLoading}
                            />
                            {siteSettings[item.key] ? "사이트에 노출" : "숨김"}
                        </ToggleLabel>
                    </InfoCard>
                ))}
            </CardGrid>
        </Panel>
    );

    const renderSiteCopy = () => {
        const activeTab = COPY_TABS.find((tab) => tab.id === copyTab) || COPY_TABS[0];
        const activeItems = siteCopyItems.filter((item) => activeTab.keys.includes(item.key));

        return (
            <Panel>
                <PanelHeader>
                    <div>
                        <PanelTitle>사이트 문구</PanelTitle>
                        <PanelSub>사용자 화면에 표시되는 문구를 페이지별로 수정합니다.</PanelSub>
                    </div>
                    <Button tone="primary" onClick={saveSiteCopy} disabled={isLoading}>문구 저장</Button>
                </PanelHeader>
                <TabList>
                    {COPY_TABS.map((tab) => (
                        <TabButton key={tab.id} active={copyTab === tab.id} onClick={() => setCopyTab(tab.id)}>
                            {tab.label}
                        </TabButton>
                    ))}
                </TabList>
                <div style={{ padding: 16, display: "grid", gap: 14 }}>
                    {activeItems.map((item) => (
                        <Field key={item.key}>
                            <Label>{item.label}</Label>
                            {item.multiline ? (
                                <TextArea
                                    value={siteCopy[item.key]}
                                    onChange={(event) => setSiteCopy((copy) => ({ ...copy, [item.key]: event.target.value }))}
                                />
                            ) : (
                                <Input
                                    value={siteCopy[item.key]}
                                    onChange={(event) => setSiteCopy((copy) => ({ ...copy, [item.key]: event.target.value }))}
                                />
                            )}
                        </Field>
                    ))}
                </div>
            </Panel>
        );
    };

    const renderStats = () => {
        const maxCount = Math.max(1, totalContent, contacts.length, categories.length);
        const rows = [
            { label: "Hero", value: contentCounts.hero_video },
            { label: "Works", value: contentCounts.works },
            { label: "Product", value: contentCounts.product },
            { label: "PR", value: contentCounts.pr },
            { label: "문의", value: contacts.length },
            { label: "카테고리", value: categories.length },
        ];
        const totalViews = content.reduce((sum, item) => sum + Number(item.view_count || 0), 0);
        const totalLikes = content.reduce((sum, item) => sum + Number(item.like_count || 0), 0);
        const topContent = [...content].sort((a, b) => Number(b.view_count || 0) - Number(a.view_count || 0)).slice(0, 5);

        return (
            <Panel>
                <PanelHeader>
                    <div>
                        <PanelTitle>운영 통계</PanelTitle>
                        <PanelSub>보고 싶은 지표만 탭으로 나누어 확인합니다.</PanelSub>
                    </div>
                </PanelHeader>
                <TabList>
                    {STATS_TABS.map((tab) => (
                        <TabButton key={tab.id} active={statsTab === tab.id} onClick={() => setStatsTab(tab.id)}>
                            {tab.label}
                        </TabButton>
                    ))}
                </TabList>
                {statsTab === "summary" && (
                    <ChartList>
                        {rows.map((row) => (
                            <ChartRow key={row.label}>
                                <strong>{row.label}</strong>
                                <Track><Fill width={(row.value / maxCount) * 100} /></Track>
                                <span>{formatNumber(row.value)}</span>
                            </ChartRow>
                        ))}
                    </ChartList>
                )}
                {statsTab === "performance" && (
                    <DashboardStack style={{ padding: 16 }}>
                        <ChartList style={{ padding: 0 }}>
                            <ChartRow><strong>활성률</strong><Track><Fill width={totalContent ? (activeContent / totalContent) * 100 : 0} /></Track><span>{totalContent ? Math.round((activeContent / totalContent) * 100) : 0}%</span></ChartRow>
                            <ChartRow><strong>총 조회</strong><Track><Fill width={100} /></Track><span>{formatNumber(totalViews)}</span></ChartRow>
                            <ChartRow><strong>총 좋아요</strong><Track><Fill width={totalViews ? (totalLikes / totalViews) * 100 : 0} /></Track><span>{formatNumber(totalLikes)}</span></ChartRow>
                        </ChartList>
                        <ActivityList style={{ padding: 0 }}>
                            {topContent.map((item) => (
                                <ActivityRow key={item.id}>
                                    <RowTitle>{item.title}</RowTitle>
                                    <RowMeta>{CONTENT_LABELS[item.content_type]} · {formatViewMetric(item.view_count)}</RowMeta>
                                </ActivityRow>
                            ))}
                            {topContent.length === 0 && <RowMeta>표시할 콘텐츠가 없습니다.</RowMeta>}
                        </ActivityList>
                    </DashboardStack>
                )}
                {statsTab === "quality" && (
                    <ActivityList>
                        {qualityIssues.slice(0, 8).map((issue) => (
                            <ActivityRow key={`${issue.id}-${issue.issue}`}>
                                <RowTitle>{issue.title}</RowTitle>
                                <RowMeta>{issue.level} · {issue.issue}</RowMeta>
                            </ActivityRow>
                        ))}
                        {qualityIssues.length === 0 && <RowMeta>확인할 품질 이슈가 없습니다.</RowMeta>}
                    </ActivityList>
                )}
            </Panel>
        );
    };

    const renderActivity = () => (
        <Panel>
            <PanelHeader>
                <div>
                    <PanelTitle>최근 작업</PanelTitle>
                    <PanelSub>이 브라우저에서 수행한 관리자 작업</PanelSub>
                </div>
            </PanelHeader>
            <ActivityList>
                {activityLog.length === 0 ? (
                    <RowMeta>아직 기록된 작업이 없습니다.</RowMeta>
                ) : (
                    activityLog.slice(0, 8).map((item) => (
                        <ActivityRow key={item.id}>
                            <RowTitle>{item.action}</RowTitle>
                            <RowMeta>{item.detail} · {formatDateTime(item.createdAt)}</RowMeta>
                        </ActivityRow>
                    ))
                )}
            </ActivityList>
        </Panel>
    );

    const renderOverview = () => {
        const pendingContacts = contacts.filter((contact) =>
            !isActive(contact.is_read) || contact.admin_status === "new" || contact.admin_status === "reviewing"
        );
        const hiddenContent = Math.max(0, totalContent - activeContent);
        const activeSiteSections = siteSettingItems.filter((item) => siteSettings[item.key]).length;

        return (
            <DashboardStack>
                <Panel>
                    <PanelHeader>
                        <div>
                            <PanelTitle>운영 요약</PanelTitle>
                            <PanelSub>콘텐츠 관리는 왼쪽 콘텐츠 메뉴에서 진행합니다.</PanelSub>
                        </div>
                        <Toolbar>
                            <Button tone="primary" onClick={() => navigateSection("content")}>콘텐츠 관리</Button>
                            <Button onClick={() => navigateSection("contacts")}>문의 확인</Button>
                        </Toolbar>
                    </PanelHeader>
                    <SummaryGrid>
                        <InfoCard>
                            <RowMeta>노출 콘텐츠</RowMeta>
                            <KpiValue>{formatNumber(activeContent)}</KpiValue>
                            <RowMeta>전체 {formatNumber(totalContent)}개 중 노출</RowMeta>
                        </InfoCard>
                        <InfoCard>
                            <RowMeta>미확인 문의</RowMeta>
                            <KpiValue>{formatNumber(unreadContacts)}</KpiValue>
                            <RowMeta>{formatNumber(pendingContacts.length)}건 처리 대기</RowMeta>
                        </InfoCard>
                        <InfoCard>
                            <RowMeta>품질 확인</RowMeta>
                            <KpiValue>{formatNumber(qualityIssues.length)}</KpiValue>
                            <RowMeta>썸네일, 설명, 분류 확인</RowMeta>
                        </InfoCard>
                        <InfoCard>
                            <RowMeta>사이트 섹션</RowMeta>
                            <KpiValue>{formatNumber(activeSiteSections)}</KpiValue>
                            <RowMeta>{formatNumber(siteSettingItems.length)}개 중 노출</RowMeta>
                        </InfoCard>
                    </SummaryGrid>
                </Panel>

                <SplitGrid>
                    <Panel>
                        <PanelHeader>
                            <div>
                                <PanelTitle>확인할 일</PanelTitle>
                                <PanelSub>오늘 먼저 보면 좋은 항목</PanelSub>
                            </div>
                        </PanelHeader>
                        <ActivityList>
                            {pendingContacts.length > 0 && (
                                <ActivityRow>
                                    <RowTitle>처리 대기 문의 {formatNumber(pendingContacts.length)}건</RowTitle>
                                    <RowMeta>문의 메뉴에서 상태와 내부 메모를 정리하세요.</RowMeta>
                                </ActivityRow>
                            )}
                            {qualityIssues.length > 0 && (
                                <ActivityRow>
                                    <RowTitle>품질 확인 콘텐츠 {formatNumber(qualityIssues.length)}건</RowTitle>
                                    <RowMeta>{qualityIssues[0].title} · {qualityIssues[0].issue}</RowMeta>
                                </ActivityRow>
                            )}
                            {hiddenContent > 0 && (
                                <ActivityRow>
                                    <RowTitle>숨김 콘텐츠 {formatNumber(hiddenContent)}건</RowTitle>
                                    <RowMeta>공개가 필요한 콘텐츠는 콘텐츠 메뉴에서 노출 상태를 변경하세요.</RowMeta>
                                </ActivityRow>
                            )}
                            {pendingContacts.length === 0 && qualityIssues.length === 0 && hiddenContent === 0 && (
                                <RowMeta>지금 바로 확인할 운영 이슈가 없습니다.</RowMeta>
                            )}
                        </ActivityList>
                    </Panel>

                    <Panel>
                        <PanelHeader>
                            <div>
                                <PanelTitle>사이트 노출 상태</PanelTitle>
                                <PanelSub>포트폴리오 섹션 표시 여부</PanelSub>
                            </div>
                            <Button onClick={() => navigateSection("site")}>설정</Button>
                        </PanelHeader>
                        <ActivityList>
                            {siteSettingItems.map((item) => (
                                <ActivityRow key={item.key}>
                                    <RowTitle>{item.title}</RowTitle>
                                    <RowMeta>{siteSettings[item.key] ? "노출 중" : "숨김"} · {item.priority}</RowMeta>
                                </ActivityRow>
                            ))}
                        </ActivityList>
                    </Panel>
                </SplitGrid>

                {renderActivity()}
            </DashboardStack>
        );
    };

    const renderCurrentSection = () => {
        if (activeSection === "content") return renderContentTable();
        if (activeSection === "contacts") return renderContacts();
        if (activeSection === "categories") return renderCategories();
        if (activeSection === "site") return renderSiteSettings();
        if (activeSection === "copy") return renderSiteCopy();
        if (activeSection === "stats") return <>{renderStats()}{renderActivity()}</>;
        return renderOverview();
    };

    if (!isLoggedIn) {
        return (
            <LoginScreen>
                <LoginPanel onSubmit={handleLogin}>
                    <LoginTitle>NO:ON Admin</LoginTitle>
                    <LoginSub>콘텐츠 운영 콘솔</LoginSub>
                    {error && <Message tone="error">{error}</Message>}
                    <Field>
                        <Label htmlFor="admin-username">아이디</Label>
                        <Input id="admin-username" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
                    </Field>
                    <div style={{ height: 12 }} />
                    <Field>
                        <Label htmlFor="admin-password">비밀번호</Label>
                        <Input id="admin-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" />
                    </Field>
                    <div style={{ height: 20 }} />
                    <Button tone="primary" type="submit" disabled={isLoading} style={{ width: "100%" }}>
                        {isLoading ? "확인 중" : "로그인"}
                    </Button>
                </LoginPanel>
            </LoginScreen>
        );
    }

    return (
        <AdminShell>
            <Layout>
                <Sidebar>
                    <Brand>
                        <BrandName>NO:ON Admin</BrandName>
                        <BrandMeta>Operations Console</BrandMeta>
                    </Brand>
                    <NavList>
                        {NAV_ITEMS.map((item) => (
                            <NavButton key={item.id} active={activeSection === item.id} onClick={() => navigateSection(item.id)}>
                                <NavTitle>{item.label}</NavTitle>
                                <NavDesc>{item.description}</NavDesc>
                            </NavButton>
                        ))}
                    </NavList>
                    <SidebarFooter>
                        <Button onClick={fetchData}>새로고침</Button>
                        <Button tone="danger" onClick={handleLogout}>로그아웃</Button>
                    </SidebarFooter>
                </Sidebar>
                <Main>
                    <Topbar>
                        <div>
                            <PageTitle>{NAV_ITEMS.find((item) => item.id === activeSection)?.label}</PageTitle>
                            <PageMeta>{isLoading ? "동기화 중" : `마지막 화면 갱신 ${new Date().toLocaleTimeString("ko-KR")}`}</PageMeta>
                        </div>
                        <Toolbar>
                            {activeSection === "content" && <Button onClick={() => openNewContent("works")}>콘텐츠 추가</Button>}
                            {activeSection === "categories" && <Button tone="primary" onClick={openNewCategory}>카테고리 추가</Button>}
                            {activeSection === "site" && <Button onClick={fetchData}>설정 새로고침</Button>}
                            {activeSection === "copy" && <Button tone="primary" onClick={saveSiteCopy} disabled={isLoading}>문구 저장</Button>}
                        </Toolbar>
                    </Topbar>
                    {error && <Message tone="error">{error}</Message>}
                    {success && <Message tone="success">{success}</Message>}
                    {activeSection === "overview" && renderKpis()}
                    {renderCurrentSection()}
                </Main>
            </Layout>

            {contentModalOpen && (
                <ModalBackdrop>
                    <ModalPanel>
                        <ModalHeader>
                            <ModalTitle>{editingContent ? "콘텐츠 수정" : "콘텐츠 추가"}</ModalTitle>
                            <Button onClick={() => setContentModalOpen(false)}>닫기</Button>
                        </ModalHeader>
                        <FormGrid onSubmit={submitContent}>
                            <TwoCols>
                                <Field>
                                    <Label>유형</Label>
                                    <Select value={contentForm.content_type} onChange={(event) => setContentForm((form) => ({ ...form, content_type: event.target.value as ContentType }))}>
                                        {Object.entries(CONTENT_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </Select>
                                </Field>
                                <Field>
                                    <Label>카테고리</Label>
                                    <Select value={contentForm.category_id} onChange={(event) => setContentForm((form) => ({ ...form, category_id: event.target.value }))}>
                                        <option value="">카테고리 없음</option>
                                        {categories.map((category) => (
                                            <option key={category.id} value={category.id}>{category.name}</option>
                                        ))}
                                    </Select>
                                </Field>
                            </TwoCols>
                            <Field>
                                <Label>YouTube URL</Label>
                                <Toolbar>
                                    <Input style={{ flex: 1 }} value={contentForm.youtube_url} onChange={(event) => setContentForm((form) => ({ ...form, youtube_url: event.target.value }))} />
                                    <Button type="button" onClick={fetchYouTubeInfo}>정보 가져오기</Button>
                                </Toolbar>
                            </Field>
                            <Field>
                                <Label>제목</Label>
                                <Input value={contentForm.title} onChange={(event) => setContentForm((form) => ({ ...form, title: event.target.value }))} required />
                            </Field>
                            <Field>
                                <Label>설명</Label>
                                <TextArea value={contentForm.description} onChange={(event) => setContentForm((form) => ({ ...form, description: event.target.value }))} />
                            </Field>
                            <Field>
                                <Label>커스텀 설명</Label>
                                <TextArea value={contentForm.custom_description} onChange={(event) => setContentForm((form) => ({ ...form, custom_description: event.target.value }))} />
                            </Field>
                            <Field>
                                <Label>썸네일 URL</Label>
                                <Toolbar>
                                    <Input style={{ flex: 1 }} value={contentForm.custom_thumbnail} onChange={(event) => {
                                        setThumbnailStatus("idle");
                                        setContentForm((form) => ({ ...form, custom_thumbnail: event.target.value }));
                                    }} />
                                    <Button type="button" onClick={() => validateThumbnail()}>썸네일 검사</Button>
                                    <Status active={thumbnailStatus === "valid"} warn={thumbnailStatus === "invalid"}>
                                        {thumbnailStatus === "checking" ? "검사 중" : thumbnailStatus === "valid" ? "정상" : thumbnailStatus === "invalid" ? "오류" : "대기"}
                                    </Status>
                                </Toolbar>
                            </Field>
                            <TwoCols>
                                <Field>
                                    <Label>예약 공개일</Label>
                                    <Input type="datetime-local" value={contentForm.custom_published_at} onChange={(event) => setContentForm((form) => ({ ...form, custom_published_at: event.target.value }))} />
                                </Field>
                                <Field>
                                    <Label>정렬 순서</Label>
                                    <Input type="number" value={contentForm.sort_order} onChange={(event) => setContentForm((form) => ({ ...form, sort_order: Number(event.target.value) }))} />
                                </Field>
                            </TwoCols>
                            <ToggleLabel>
                                <Checkbox type="checkbox" checked={contentForm.is_active} onChange={(event) => setContentForm((form) => ({ ...form, is_active: event.target.checked }))} />
                                사이트에 노출
                            </ToggleLabel>
                            <ModalFooter>
                                <Button type="button" onClick={() => setPreviewContent({
                                    ...(editingContent || content[0]),
                                    ...makeContentPayload(editingContent || content[0] || ({ id: 0 } as Content), contentForm),
                                    id: editingContent?.id || 0,
                                    youtube_id: editingContent?.youtube_id || "",
                                    thumbnail_url: contentForm.custom_thumbnail,
                                    custom_thumbnail: contentForm.custom_thumbnail,
                                    view_count: editingContent?.view_count || 0,
                                    like_count: editingContent?.like_count || 0,
                                    created_at: editingContent?.created_at || new Date().toISOString(),
                                    updated_at: new Date().toISOString(),
                                } as Content)}>미리보기</Button>
                                <Button type="submit" tone="primary" disabled={isLoading}>{isLoading ? "저장 중" : "저장"}</Button>
                            </ModalFooter>
                        </FormGrid>
                    </ModalPanel>
                </ModalBackdrop>
            )}

            {categoryModalOpen && (
                <ModalBackdrop>
                    <ModalPanel>
                        <ModalHeader>
                            <ModalTitle>{editingCategory ? "카테고리 수정" : "카테고리 추가"}</ModalTitle>
                            <Button onClick={() => setCategoryModalOpen(false)}>닫기</Button>
                        </ModalHeader>
                        <FormGrid onSubmit={submitCategory}>
                            <Field>
                                <Label>이름</Label>
                                <Input value={categoryForm.name} onChange={(event) => setCategoryForm((form) => ({ ...form, name: event.target.value }))} required />
                            </Field>
                            <Field>
                                <Label>설명</Label>
                                <TextArea value={categoryForm.description} onChange={(event) => setCategoryForm((form) => ({ ...form, description: event.target.value }))} />
                            </Field>
                            <TwoCols>
                                <Field>
                                    <Label>색상</Label>
                                    <Input type="color" value={categoryForm.color} onChange={(event) => setCategoryForm((form) => ({ ...form, color: event.target.value }))} />
                                </Field>
                                <Field>
                                    <Label>정렬</Label>
                                    <Input type="number" value={categoryForm.sort_order} onChange={(event) => setCategoryForm((form) => ({ ...form, sort_order: Number(event.target.value) }))} />
                                </Field>
                            </TwoCols>
                            <ModalFooter>
                                <Button type="submit" tone="primary" disabled={isLoading}>저장</Button>
                            </ModalFooter>
                        </FormGrid>
                    </ModalPanel>
                </ModalBackdrop>
            )}

            {previewContent && (
                <ModalBackdrop>
                    <ModalPanel>
                        <ModalHeader>
                            <ModalTitle>콘텐츠 미리보기</ModalTitle>
                            <Button onClick={() => setPreviewContent(null)}>닫기</Button>
                        </ModalHeader>
                        <ModalBody>
                            <PreviewThumb src={getReliableThumbnailUrl(previewContent.custom_thumbnail || previewContent.thumbnail_url)} alt={previewContent.title} onError={(event) => handleThumbnailError(event.currentTarget)} />
                            <CardGrid>
                                <InfoCard>
                                    <RowMeta>유형</RowMeta>
                                    <RowTitle>{CONTENT_LABELS[previewContent.content_type]}</RowTitle>
                                </InfoCard>
                                <InfoCard>
                                    <RowMeta>상태</RowMeta>
                                    <RowTitle>{isActive(previewContent.is_active) ? "노출" : "비노출"}</RowTitle>
                                </InfoCard>
                            </CardGrid>
                            <div>
                                <h3 style={{ margin: "0 0 8px" }}>{previewContent.title}</h3>
                                <p style={{ margin: 0, color: "#495566", lineHeight: 1.6 }}>{previewContent.custom_description || previewContent.description || "설명이 없습니다."}</p>
                            </div>
                            <Toolbar>
                                {previewContent.youtube_url && <Button onClick={() => window.open(previewContent.youtube_url, "_blank", "noopener,noreferrer")}>YouTube 열기</Button>}
                            </Toolbar>
                        </ModalBody>
                    </ModalPanel>
                </ModalBackdrop>
            )}
        </AdminShell>
    );
};

export default Admin;
